import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import { BreadcrumbNav } from "components/molecules";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import Populasi from "services/Populasi";
import { MonthOptions, PageMode } from "utils/constant";
import VectorControlTabs from "./VectorControlTabs";
import { useVectorControlTabs } from "./VectorControlUtils";
import Button from "components/atoms/Button";
import KelambuDistribution from "services/KelambuDistribution";
import IRSResult from "services/IRSResult";
import { toast } from "react-toastify";
import KelambuMassalTotalFields from "./KelambuMassalTotalFields";
import KelambuMassalFokusFields from "./KelambuMassalFokusFields";
import KelambuPE125Fields from "./KelambuPE125Fields";
import KelambuKLBBencanaFields from "./KelambuKLBBencanaFields";
import IRSFields from "./IRSFields";
import { removeDataFromLocalStorage } from "utils/common";
import { toInt } from "validator";

const VectorReportService = new IRSResult();
const PopuplationService = new Populasi();
const DistribusiKelambuService = new KelambuDistribution();

const INSECTICIDE_TYPE_OPTIONS = [
  { label: "Organophosphate", value: "Organophosphate" },
  { label: "Karbamat", value: "Karbamat" },
  { label: "Piretroid", value: "Piretroid" },
  { label: "Neonikotinoid", value: "Neonikotinoid" },
];

const INSECTICIDE_UNIT_OPTIONS = [
  { label: "Kg", value: "Kg" },
  { label: "Liter", value: "Liter" },
];

const YES_NO_OPTIONS = [
  { label: "Ya", value: "Ya" },
  { label: "Tidak", value: "Tidak" },
];

const debounce = (fn, wait = 600) => {
  let t;
  const debounced = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(t);
  return debounced;
};

const mismatchShownRef = { current: {} };

const notifyCompareReceivedVsDesk = debounce((label, received, desk, key) => {
  const rec = parseInt(received || 0, 10);
  const dsk = parseInt(desk || 0, 10);

  if (!(rec > 0 && dsk > 0)) return;
  if (rec === dsk) return;

  if (!mismatchShownRef.current[key]) {
    mismatchShownRef.current[key] = true;

    toast.warning(
      `${label}: jumlah kelambu diterima ` +
        `${rec > dsk ? "lebih besar" : "lebih kecil"} dari hasil desk.`,
      { autoClose: 6000 },
    );

    setTimeout(() => {
      mismatchShownRef.current[key] = false;
    }, 1500);
  }
}, 600);

const safeToInt = (v) =>
  v === "" || v === null || v === undefined ? 0 : parseInt(v, 10) || 0;
const isEmptyNumber = (v) => v === "" || v === null || v === undefined;

const FormComponent = ({ pageMode, changeMode }) => {
  const [urbanvillages, setUrbanVillages] = useState([]);

  const { activeTab, handleTabChange } = useVectorControlTabs();

  const [
    isDisabledNetReceivedKelambuMassal,
    setIsDisabledNetReceivedKelambuMassal,
  ] = useState(false);
  const [
    isDisabledNetReceivedKelambuMassalFokus,
    setIsDisabledNetReceivedKelambuMassalFokus,
  ] = useState(false);

  // State untuk validasi real-time di edit form
  const [editFormValidationError, setEditFormValidationError] = useState("");

  const [formData, setFormData] = useState({
    urbanvillage_id: "",
    total_population_urbanvillage: "",

    kelambu_massal_total: {
      net_distribution_date: "",
      total_mosquito_nets_received: "",
      total_net_distribution: "",
      total_mosquito_desk: "",
      total_population_target: "",
      total_nets_distributed_to_pregnant_women: "",
      is_dusun_level_distribution: "",
      total_sub_villages: "",
      sub_villages_data: [],
    },

    kelambu_massal_fokus: {
      net_distribution_date: "",
      total_mosquito_nets_received: "",
      total_net_distribution: "",
      total_mosquito_desk: "",
      total_population_target: "",
      total_nets_distributed_to_pregnant_women: "",
      is_dusun_level_distribution: "",
      total_sub_villages: "",
      sub_villages_data: [],
    },

    kelambu_pe_125: {
      net_distribution_date: "",
      total_mosquito_nets_received: "",
      total_net_distribution: "",
      total_mosquito_desk: "",
      total_population_target: "",
      total_nets_distributed_to_pregnant_women: "",
      is_dusun_level_distribution: "",
      total_sub_villages: "",
      sub_villages_data: [],
    },

    kelambu_klb_bencana: {
      net_distribution_date: "",
      total_mosquito_nets_received: "",
      total_net_distribution: "",
      total_mosquito_desk: "",
      total_population_target: "",
      total_nets_distributed_to_pregnant_women: "",
      is_dusun_level_distribution: "",
      total_sub_villages: "",
      sub_villages_data: [],
    },

    irs: {
      irs_date: "",
      insecticide_type: "",
      total_insecticide: "",
      total_insecticide_unit: "",
      total_targeted_house: "",
      total_sprayed_house: "",
      is_dusun_level_intervention: "",
      total_sub_villages: "",
      sub_villages_data: [],
    },
  });

  useEffect(() => {
    notifyCompareReceivedVsDesk(
      "Kelambu Massal Total",
      formData.kelambu_massal_total.total_mosquito_nets_received,
      formData.kelambu_massal_total.total_mosquito_desk,
      "total",
    );
  }, [
    formData.kelambu_massal_total.total_mosquito_nets_received,
    formData.kelambu_massal_total.total_mosquito_desk,
  ]);

  useEffect(() => {
    notifyCompareReceivedVsDesk(
      "Kelambu Massal Fokus",
      formData.kelambu_massal_fokus.total_mosquito_nets_received,
      formData.kelambu_massal_fokus.total_mosquito_desk,
      "fokus",
    );
  }, [
    formData.kelambu_massal_fokus.total_mosquito_nets_received,
    formData.kelambu_massal_fokus.total_mosquito_desk,
  ]);

  useEffect(() => {
    notifyCompareReceivedVsDesk(
      "Kelambu PE 1-2-5",
      formData.kelambu_pe_125.total_mosquito_nets_received,
      formData.kelambu_pe_125.total_mosquito_desk,
      "pe125",
    );
  }, [
    formData.kelambu_pe_125.total_mosquito_nets_received,
    formData.kelambu_pe_125.total_mosquito_desk,
  ]);

  useEffect(() => {
    notifyCompareReceivedVsDesk(
      "Kelambu KLB/Bencana",
      formData.kelambu_klb_bencana.total_mosquito_nets_received,
      formData.kelambu_klb_bencana.total_mosquito_desk,
      "klb",
    );
  }, [
    formData.kelambu_klb_bencana.total_mosquito_nets_received,
    formData.kelambu_klb_bencana.total_mosquito_desk,
  ]);

  const [editFormData, setEditFormData] = useState({
    id: "",
    urbanvillage_id: "",
    sub_village: "",
    total_population_urbanvillage: "",
    sub_village_population: "",

    // Kelambu fields
    net_distribution_date: "",
    total_mosquito_desk: "",
    total_mosquito_nets_received: "",
    total_net_distribution: "",
    nets_distributed_sub_village: "",
    is_dusun_level_distribution: "",
    total_sub_villages: "",
    total_population_target: "",
    total_nets_distributed_to_pregnant_women: "",

    // IRS fields
    irs_date: "",
    insecticide_type: "",
    total_insecticide: "",
    total_insecticide_unit: "",
    total_targeted_house: "",
    total_sprayed_house: "",
    targeted_houses_sub_village: "",
    sprayed_houses_sub_village: "",
    is_dusun_level_intervention: "",

    // Control type
    vector_controll_type: "",
  });

  const [showFields, setShowFields] = useState({
    "Kelambu Massal Total": false,
    "Kelambu Massal Fokus": false,
    "Kelambu PE 1-2-5": false,
    "Kelambu KLB/Bencana": false,
    IRS: false,
  });

  const { currentUser } = useSelector((state) => state.authReducer);

  const memoizedValues = useMemo(
    () => ({
      kelambuMassalTotal: {
        isDusunLevel: formData.kelambu_massal_total.is_dusun_level_distribution
          ? {
              label: formData.kelambu_massal_total.is_dusun_level_distribution,
              value: formData.kelambu_massal_total.is_dusun_level_distribution,
            }
          : null,
      },
      kelambuMassalFokus: {
        isDusunLevel: formData.kelambu_massal_fokus.is_dusun_level_distribution
          ? {
              label: formData.kelambu_massal_fokus.is_dusun_level_distribution,
              value: formData.kelambu_massal_fokus.is_dusun_level_distribution,
            }
          : null,
      },
      kelambuPE125: {
        isDusunLevel: formData.kelambu_pe_125.is_dusun_level_distribution
          ? {
              label: formData.kelambu_pe_125.is_dusun_level_distribution,
              value: formData.kelambu_pe_125.is_dusun_level_distribution,
            }
          : null,
      },
      kelambuKlbBencana: {
        isDusunLevel: formData.kelambu_klb_bencana.is_dusun_level_distribution
          ? {
              label: formData.kelambu_klb_bencana.is_dusun_level_distribution,
              value: formData.kelambu_klb_bencana.is_dusun_level_distribution,
            }
          : null,
      },
      irs: {
        isDusunLevel: formData.irs.is_dusun_level_intervention
          ? {
              label: formData.irs.is_dusun_level_intervention,
              value: formData.irs.is_dusun_level_intervention,
            }
          : null,
        insecticideType: formData.irs.insecticide_type
          ? {
              label: formData.irs.insecticide_type,
              value: formData.irs.insecticide_type,
            }
          : null,
        insecticideUnit: formData.irs.total_insecticide_unit
          ? {
              label: formData.irs.total_insecticide_unit,
              value: formData.irs.total_insecticide_unit,
            }
          : null,
      },
    }),
    [formData],
  );

  const villageOptions = useMemo(
    () =>
      urbanvillages.map((item) => ({
        label:
          item.urbanvillage_id?.split?.("|")?.[1] ||
          item.urbanvillage_name ||
          String(item.urbanvillage_id),
        value: item.urbanvillage_id,
        _raw: item,
      })),
    [urbanvillages],
  );

  const getOptionByValue = useCallback(
    (val) => villageOptions.find((o) => o.value === val) || null,
    [villageOptions],
  );

  const loadEditDataFromStorage = useCallback(() => {
    try {
      const storedData = localStorage.getItem("EDIT_VECTOR_CONTROL_RESULT");
      if (storedData) {
        const parsedData = JSON.parse(storedData);

        setEditFormData({
          id: parsedData.id || "",
          urbanvillage_id: parsedData.urbanvillage_id || "",
          sub_village: parsedData.sub_village || "",
          total_population_urbanvillage:
            parsedData.total_population_urbanvillage || "",
          sub_village_population: parsedData.sub_village_population || "",

          // Kelambu fields
          net_distribution_date: parsedData.net_distribution_date || "",
          total_mosquito_desk: parsedData.total_mosquito_desk || "",
          total_mosquito_nets_received:
            parsedData.total_mosquito_nets_received || "",
          total_net_distribution: parsedData.total_net_distribution || "",
          nets_distributed_sub_village:
            parsedData.nets_distributed_sub_village || "",
          is_dusun_level_distribution:
            parsedData.is_dusun_level_distribution || "",
          total_sub_villages: parsedData.total_sub_villages || "",
          total_population_target: parsedData.total_population_target || "",
          total_nets_distributed_to_pregnant_women:
            parsedData.total_nets_distributed_to_pregnant_women || "",

          // IRS fields
          irs_date: parsedData.irs_date || "",
          insecticide_type: parsedData.insecticide_type || "",
          total_insecticide: parsedData.total_insecticide || "",
          total_insecticide_unit: parsedData.total_insecticide_unit || "",
          total_targeted_house: parsedData.total_targeted_house || "",
          total_sprayed_house: parsedData.total_sprayed_house || "",
          targeted_houses_sub_village:
            parsedData.targeted_houses_sub_village || "",
          sprayed_houses_sub_village:
            parsedData.sprayed_houses_sub_village || "",
          is_dusun_level_intervention:
            parsedData.is_dusun_level_intervention || "",

          // Control type
          vector_controll_type: parsedData.vector_controll_type || "",
        });

        return parsedData;
      }
    } catch (error) {
      console.error("Error loading edit data:", error);

      toast.error("Error memuat data edit");
    }
    return null;
  }, []);

  const fetchUrbanVillagesByUser = useCallback(async () => {
    try {
      await PopuplationService.getAll({
        page: 1,
        limit: 99999,
        searchBy: `fasyankes_id,year`,
        searchValue: `${currentUser.fasyankes_id},${new Date().getFullYear()}`,
      })
        .then((res) => {
          setUrbanVillages(res.populations || []);
        })
        .catch((err) => {
          console.error(err);
        });
    } catch (error) {
      console.log(error);
    }
  }, [currentUser]);

  const fetchTotalKelambuDesk = async (urbanvillage_id, year) => {
    try {
      await DistribusiKelambuService.totalDesk(urbanvillage_id, year)
        .then((res) => {
          setFormData((prev) => ({
            ...prev,
            kelambu_massal_total: {
              ...prev.kelambu_massal_total,
              total_mosquito_desk: res.kelambu_massal_desk || 0,
              total_population_target: res.total_target_population || 0,
              total_nets_distributed_to_pregnant_women:
                res.total_nets_distributed_to_pregnant_women || 0,
            },
            kelambu_massal_fokus: {
              ...prev.kelambu_massal_fokus,
              total_mosquito_desk: res.kelambu_fokus_desk || 0,
              total_population_target: res.total_target_population || 0,
              total_nets_distributed_to_pregnant_women:
                res.total_nets_distributed_to_pregnant_women || 0,
            },
            kelambu_pe_125: {
              ...prev.kelambu_pe_125,
              // total_population_target: res.total_target_population || 0,
              // total_nets_distributed_to_pregnant_women:
              //   res.total_nets_distributed_to_pregnant_women || 0,
            },
            kelambu_klb_bencana: {
              ...prev.kelambu_klb_bencana,
              // total_population_target: res.total_target_population || 0,
              // total_nets_distributed_to_pregnant_women:
              //   res.total_nets_distributed_to_pregnant_women || 0,
            },
          }));
        })
        .catch((err) => {
          console.log(err);
        });
    } catch (error) {
      console.log(error);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInputChange = (e, fieldGroup = null) => {
    const { name, value } = e.target;

    if (fieldGroup) {
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          [fieldGroup]: {
            ...prev[fieldGroup],
            [name]: value,
          },
        };

        if (
          (fieldGroup === "kelambu_massal_total" ||
            fieldGroup === "kelambu_massal_fokus" ||
            fieldGroup === "kelambu_pe_125" ||
            fieldGroup === "kelambu_klb_bencana" ||
            fieldGroup === "irs") &&
          name === "total_sub_villages"
        ) {
          const totalSubVillages = parseInt(value) || 0;
          const currentSubVillages = prev[fieldGroup].sub_villages_data || [];

          const newSubVillagesData = Array.from(
            { length: totalSubVillages },
            (_, index) => {
              if (fieldGroup === "irs") {
                return (
                  currentSubVillages[index] || {
                    village_name: "",
                    population: "",
                    targeted_houses: "",
                    sprayed_houses: "",
                  }
                );
              } else {
                return (
                  currentSubVillages[index] || {
                    village_name: "",
                    population: "",
                    nets_distributed: "",
                  }
                );
              }
            },
          );

          newFormData[fieldGroup].sub_villages_data = newSubVillagesData;
        }

        return newFormData;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const fetchPrevKelambuMassalDiterimaDesa = async (
    month,
    vector_control_type,
    forceUrbanVillageId,
  ) => {
    const uvId = forceUrbanVillageId ?? formData.urbanvillage_id;
    if (
      !uvId ||
      !["Kelambu Massal Total", "Kelambu Massal Fokus"].includes(
        vector_control_type,
      )
    ) {
      return;
    }

    try {
      const response = await VectorReportService.getPrevTotalNetDistributed({
        month,
        vector_controll_type: vector_control_type,
        fasyankes_id: currentUser.fasyankes_id,
        urbanvillage_id: uvId,
      });

      if (response?.vector_controll_type === "Kelambu Massal Total") {
        setFormData((prev) => ({
          ...prev,
          kelambu_massal_total: {
            ...prev.kelambu_massal_total,
            total_mosquito_nets_received:
              response?.total_mosquito_nets_received ?? "",
          },
        }));
        setIsDisabledNetReceivedKelambuMassal(
          Boolean(response?.total_mosquito_nets_received),
        );
      }

      if (response?.vector_controll_type === "Kelambu Massal Fokus") {
        setFormData((prev) => ({
          ...prev,
          kelambu_massal_fokus: {
            ...prev.kelambu_massal_fokus,
            total_mosquito_nets_received:
              response?.total_mosquito_nets_received ?? "",
          },
        }));
        setIsDisabledNetReceivedKelambuMassalFokus(
          Boolean(response?.total_mosquito_nets_received),
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddButton = (tabKey) => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, "0");

    fetchPrevKelambuMassalDiterimaDesa(`${year}-${month}`, tabKey);

    setShowFields((prev) => ({
      ...prev,
      [tabKey]: true,
    }));
  };

  const handleTabChangeWithData = (tabKey) => {
    handleTabChange(tabKey);
  };

  const convertEmptyToNull = (obj) => {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value === "" || value === undefined) {
        result[key] = null;
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        result[key] = convertEmptyToNull(value);
      } else {
        result[key] = value;
      }
    }
    return result;
  };

  const sumDusunNets = (subVillages = []) =>
    subVillages.reduce((sum, v) => sum + parseInt(v && v.nets_distributed), 0);

  function validateDusunVsReceivedAndTotal(data) {
    const errs = [];
    const totalDusun = sumDusunNets((data && data.sub_villages_data) || []);
    const totalReceived = parseInt(data && data.total_mosquito_nets_received);
    const totalDistributedVillage = parseInt(
      data && data.total_net_distribution,
    );

    if (totalDusun > totalReceived) {
      errs.push(
        `Total kelambu terdistribusi tingkat dusun (${totalDusun.toLocaleString()}) ` +
          `tidak boleh melebihi jumlah kelambu yang diterima desa (${totalReceived.toLocaleString()}).`,
      );
    }

    if (totalDistributedVillage > 0 && totalDusun > totalDistributedVillage) {
      errs.push(
        `Total kelambu terdistribusi tingkat dusun (${totalDusun.toLocaleString()}) ` +
          `tidak boleh melebihi jumlah kelambu terdistribusi  ` +
          `(${totalDistributedVillage.toLocaleString()}).`,
      );
    }

    return errs;
  }

  const validateKelambuMassalTotal = (data) => {
    const errors = [];
    if (!data.net_distribution_date) {
      errors.push("Bulan distribusi kelambu harus diisi");
    }
    if (!data.total_mosquito_nets_received) {
      errors.push("Jumlah kelambu yang diterima desa harus diisi");
    }
    if (isEmptyNumber(data.total_nets_distributed_to_pregnant_women)) {
      errors.push("Jumlah kelambu terdistribusi kepada ibu hamil harus diisi");
    }
    if (isEmptyNumber(data.total_net_distribution)) {
      errors.push("Jumlah kelambu terdistribusi harus diisi");
    }

    if (data.is_dusun_level_distribution === "Ya") {
      if (!data.total_sub_villages || parseInt(data.total_sub_villages) === 0) {
        errors.push(
          "Jumlah Dusun harus diisi jika memilih distribusi sampai tingkat dusun",
        );
      } else {
        const subVillagesData = data.sub_villages_data || [];
        for (let i = 0; i < parseInt(data.total_sub_villages); i++) {
          const village = subVillagesData[i] || {};
          if (!village.village_name) {
            errors.push(`Nama dusun ${i + 1} harus diisi`);
          }
          if (!village.population) {
            errors.push(`Jumlah penduduk dusun ${i + 1} harus diisi`);
          }
          if (isEmptyNumber(village.nets_distributed)) {
            errors.push(
              `Jumlah Kelambu Terdistribusi Dusun ${i + 1} harus diisi`,
            );
          }
        }
      }

      errors.push(...validateDusunVsReceivedAndTotal(data));
    }
    return errors;
  };

  const validateKelambuMassalFokus = (data) => {
    const errors = [];
    if (!data.net_distribution_date) {
      errors.push("Bulan distribusi kelambu harus diisi");
    }
    if (!data.total_mosquito_nets_received) {
      errors.push("Jumlah Kelambu Yang Diterima harus diisi");
    }
    if (!data.total_population_target) {
      errors.push("Jumlah penduduk sasaran harus diisi");
    }
    if (isEmptyNumber(data.total_net_distribution)) {
      errors.push("Jumlah Kelambu Terdistribusi harus diisi");
    }

    if (data.is_dusun_level_distribution === "Ya") {
      if (!data.total_sub_villages || parseInt(data.total_sub_villages) === 0) {
        errors.push(
          "Jumlah Dusun harus diisi jika memilih distribusi sampai tingkat dusun",
        );
      } else {
        const subVillagesData = data.sub_villages_data || [];
        for (let i = 0; i < parseInt(data.total_sub_villages); i++) {
          const village = subVillagesData[i] || {};
          if (!village.village_name) {
            errors.push(`Nama dusun ${i + 1} harus diisi`);
          }
          if (!village.population) {
            errors.push(`Jumlah penduduk dusun ${i + 1} harus diisi`);
          }
          if (isEmptyNumber(village.nets_distributed)) {
            errors.push(
              `Jumlah Kelambu Terdistribusi Dusun ${i + 1} harus diisi`,
            );
          }
        }
      }

      errors.push(...validateDusunVsReceivedAndTotal(data));
    }
    return errors;
  };

  const validateKelambuPE125 = (data) => {
    const errors = [];
    if (!data.net_distribution_date) {
      errors.push("Tanggal distribusi kelambu harus diisi");
    }
    if (!data.total_mosquito_nets_received) {
      errors.push("Jumlah Kelambu Yang Diterima harus diisi");
    }
    if (!data.total_population_target) {
      errors.push("Jumlah penduduk sasaran harus diisi");
    }
    if (isEmptyNumber(data.total_net_distribution)) {
      errors.push("Jumlah Kelambu Terdistribusi harus diisi");
    }

    if (data.is_dusun_level_distribution === "Ya") {
      if (!data.total_sub_villages || parseInt(data.total_sub_villages) === 0) {
        errors.push(
          "Jumlah Dusun harus diisi jika memilih distribusi sampai tingkat dusun",
        );
      } else {
        const subVillagesData = data.sub_villages_data || [];
        for (let i = 0; i < parseInt(data.total_sub_villages); i++) {
          const village = subVillagesData[i] || {};
          if (!village.village_name) {
            errors.push(`Nama dusun ${i + 1} harus diisi`);
          }
          if (!village.population) {
            errors.push(`Jumlah penduduk dusun ${i + 1} harus diisi`);
          }
          if (isEmptyNumber(village.nets_distributed)) {
            errors.push(
              `Jumlah Kelambu Terdistribusi Dusun ${i + 1} harus diisi`,
            );
          }
        }
      }

      errors.push(...validateDusunVsReceivedAndTotal(data));
    }
    return errors;
  };

  const validateKelambuKLBBencana = (data) => {
    const errors = [];
    if (!data.net_distribution_date) {
      errors.push("Tanggal distribusi kelambu harus diisi");
    }
    if (!data.total_mosquito_nets_received) {
      errors.push("Jumlah Kelambu Yang Diterima harus diisi");
    }
    if (!data.total_population_target) {
      errors.push("Jumlah penduduk sasaran harus diisi");
    }
    if (isEmptyNumber(data.total_net_distribution)) {
      errors.push("Jumlah Kelambu Terdistribusi harus diisi");
    }

    if (data.is_dusun_level_distribution === "Ya") {
      if (!data.total_sub_villages || parseInt(data.total_sub_villages) === 0) {
        errors.push(
          "Jumlah Dusun harus diisi jika memilih distribusi sampai tingkat dusun",
        );
      } else {
        const subVillagesData = data.sub_villages_data || [];
        for (let i = 0; i < parseInt(data.total_sub_villages); i++) {
          const village = subVillagesData[i] || {};
          if (!village.village_name) {
            errors.push(`Nama dusun ${i + 1} harus diisi`);
          }
          if (!village.population) {
            errors.push(`Jumlah penduduk dusun ${i + 1} harus diisi`);
          }
          if (isEmptyNumber(village.nets_distributed)) {
            errors.push(
              `Jumlah Kelambu Terdistribusi Dusun ${i + 1} harus diisi`,
            );
          }
        }
      }

      errors.push(...validateDusunVsReceivedAndTotal(data));
    }
    return errors;
  };

  const validateIRS = (data) => {
    const errors = [];
    if (!data.irs_date) {
      errors.push("Tanggal IRS harus diisi");
    }
    if (!data.insecticide_type) {
      errors.push("Jenis insektisida harus diisi");
    }
    if (!data.total_insecticide) {
      errors.push("Jumlah insektisida harus diisi");
    }
    if (!data.total_insecticide_unit) {
      errors.push("Satuan insektisida harus diisi");
    }
    if (!data.total_targeted_house) {
      errors.push("Jumlah total sasaran rumah harus diisi");
    }
    if (!data.total_sprayed_house) {
      errors.push("Jumlah total rumah yang di semprot harus diisi");
    }

    if (data.is_dusun_level_intervention === "Ya") {
      if (!data.total_sub_villages || parseInt(data.total_sub_villages) === 0) {
        errors.push(
          "Jumlah Dusun harus diisi jika memilih distribusi sampai tingkat dusun",
        );
      } else {
        const subVillagesData = data.sub_villages_data || [];
        for (let i = 0; i < parseInt(data.total_sub_villages); i++) {
          const village = subVillagesData[i] || {};
          if (!village.village_name) {
            errors.push(`Nama dusun ${i + 1} harus diisi`);
          }
          if (!village.population) {
            errors.push(`Jumlah penduduk dusun ${i + 1} harus diisi`);
          }
          if (isEmptyNumber(village.targeted_houses)) {
            errors.push(
              `Jumlah total rumah sasaran tingkat dusun ${i + 1} harus diisi`,
            );
          }
          if (isEmptyNumber(village.sprayed_houses)) {
            errors.push(
              `Jumlah rumah disemprot tingkat dusun ${i + 1} harus diisi`,
            );
          }
        }
      }

      errors.push(...validateDusunVsReceivedAndTotal(data));
    }
    return errors;
  };

  const validateEditForm = (editData) => {
    const errors = [];

    if (!editData.urbanvillage_id) {
      errors.push("Nama Desa harus dipilih");
    }

    if (editData.vector_controll_type?.includes("Kelambu")) {
      if (!editData.total_mosquito_nets_received) {
        errors.push("Jumlah Kelambu Yang Diterima harus diisi");
      }
      if (isEmptyNumber(editData.total_net_distribution)) {
        errors.push("Jumlah kelambu terdistribusi harus diisi");
      }

      {
        const received = safeToInt(editData.total_mosquito_nets_received);
        const distributed = safeToInt(editData.total_net_distribution);
        if (distributed > received) {
          errors.push(
            `Jumlah kelambu terdistribusi (${distributed.toLocaleString()}) ` +
              `tidak boleh melebihi jumlah kelambu yang diterima desa (${received.toLocaleString()}).`,
          );
        }
      }

      // validate for ibu hamil
      {
        const netsDistributedToPregnant = safeToInt(
          editData.total_nets_distributed_to_pregnant_women,
        );
        const totalDistributed = safeToInt(editData.total_net_distribution);
        if (
          totalDistributed > 0 &&
          netsDistributedToPregnant > totalDistributed
        ) {
          errors.push(
            `Jumlah kelambu terdistribusi kepada ibu hamil (${netsDistributedToPregnant.toLocaleString()}) ` +
              `tidak boleh melebihi jumlah total kelambu terdistribusi (${totalDistributed.toLocaleString()}).`,
          );
        }
      }

      if (editData.is_dusun_level_distribution === "Ya") {
        if (!editData.sub_village) {
          errors.push("Nama dusun harus diisi");
        }
        if (!editData.sub_village_population) {
          errors.push("Jumlah penduduk dusun harus diisi");
        }
        if (isEmptyNumber(editData.nets_distributed_sub_village)) {
          errors.push("Jumlah kelambu terdistribusi (dusun) harus diisi");
        }

        {
          const received = safeToInt(editData.total_mosquito_nets_received);
          const dusunDistributed = safeToInt(
            editData.nets_distributed_sub_village,
          );
          if (dusunDistributed > received) {
            errors.push(
              `Jumlah kelambu terdistribusi (dusun) (${dusunDistributed.toLocaleString()}) ` +
                `tidak boleh melebihi jumlah kelambu yang diterima desa (${received.toLocaleString()}).`,
            );
          }
        }

        {
          const totalDistributed = safeToInt(editData.total_net_distribution);
          const dusunDistributed = safeToInt(
            editData.nets_distributed_sub_village,
          );
          if (totalDistributed > 0 && dusunDistributed > totalDistributed) {
            errors.push(
              `Jumlah kelambu terdistribusi (dusun) (${dusunDistributed.toLocaleString()}) ` +
                `tidak boleh melebihi jumlah kelambu terdistribusi (${totalDistributed.toLocaleString()}).`,
            );
          }
        }
      }
    } else if (editData.vector_controll_type === "IRS") {
      if (!editData.irs_date) {
        errors.push("Tanggal IRS harus diisi");
      }
      if (!editData.insecticide_type) {
        errors.push("Jenis insektisida harus dipilih");
      }
      if (!editData.total_insecticide) {
        errors.push("Jumlah insektisida yang digunakan harus diisi");
      }
      if (!editData.total_insecticide_unit) {
        errors.push("Satuan harus dipilih");
      }
      if (!editData.total_targeted_house) {
        errors.push("Jumlah total sasaran rumah harus diisi");
      }
      if (!editData.total_sprayed_house) {
        errors.push("Jumlah total rumah yang disemprot harus diisi");
      }

      if (editData.is_dusun_level_intervention === "Ya") {
        if (!editData.sub_village) {
          errors.push("Nama dusun harus diisi");
        }
        if (!editData.sub_village_population) {
          errors.push("Jumlah penduduk dusun harus diisi");
        }
        if (!editData.targeted_houses_sub_village) {
          errors.push("Jumlah total sasaran rumah (dusun) harus diisi");
        }
        if (!editData.sprayed_houses_sub_village) {
          errors.push("Jumlah total rumah yang disemprot (dusun) harus diisi");
        }

        {
          const totalTargeted = safeToInt(editData.total_targeted_house);
          const dusunTargeted = safeToInt(editData.targeted_houses_sub_village);
          const dusunSprayed = safeToInt(editData.sprayed_houses_sub_village);

          if (dusunTargeted > totalTargeted) {
            errors.push(
              `Jumlah total sasaran rumah (dusun) (${dusunTargeted.toLocaleString()}) ` +
                `tidak boleh melebihi Jumlah total sasaran rumah (${totalTargeted.toLocaleString()}).`,
            );
          }

          if (dusunSprayed > totalTargeted) {
            errors.push(
              `Jumlah total rumah yang disemprot (dusun) (${dusunSprayed.toLocaleString()}) ` +
                `tidak boleh melebihi Jumlah total sasaran rumah (${totalTargeted.toLocaleString()}).`,
            );
          }
        }
      }
    }

    return errors;
  };

  const handleEditSubmit = async () => {
    try {
      const validationErrors = validateEditForm(editFormData);
      if (validationErrors.length > 0) {
        const errorListHTML = `
          <ul style="margin-left:16px; text-align:left;">
            ${validationErrors.map((err) => `<li>${err}</li>`).join("")}
          </ul>
        `;
        toast.error(
          <div>
            <strong>Lengkapi data berikut:</strong>
            <div dangerouslySetInnerHTML={{ __html: errorListHTML }} />
          </div>,
          { autoClose: 8000, position: "top-right" },
        );
        return;
      }
      const rawPayload = {
        ...editFormData,
        total_population_urbanvillage:
          parseInt(editFormData.total_population_urbanvillage) || 0,
        sub_village_population:
          parseInt(editFormData.sub_village_population) || 0,
        total_mosquito_desk: parseInt(editFormData.total_mosquito_desk) || 0,
        total_population_target:
          parseInt(editFormData.total_population_target) || 0,
        total_nets_distributed_to_pregnant_women:
          parseInt(editFormData.total_nets_distributed_to_pregnant_women) || 0,
        total_mosquito_nets_received:
          parseInt(editFormData.total_mosquito_nets_received) || 0,
        total_net_distribution:
          parseInt(editFormData.total_net_distribution) || 0,
        nets_distributed_sub_village:
          parseInt(editFormData.nets_distributed_sub_village) || 0,
        total_sub_villages: parseInt(editFormData.total_sub_villages) || 0,
        total_insecticide: parseInt(editFormData.total_insecticide) || 0,
        total_targeted_house: parseInt(editFormData.total_targeted_house) || 0,
        total_sprayed_house: parseInt(editFormData.total_sprayed_house) || 0,
        targeted_houses_sub_village:
          parseInt(editFormData.targeted_houses_sub_village) || 0,
        sprayed_houses_sub_village:
          parseInt(editFormData.sprayed_houses_sub_village) || 0,
      };

      const payload = convertEmptyToNull(rawPayload);

      await VectorReportService.update(payload, editFormData.id)
        .then((res) => {
          toast.success("Data berhasil diperbarui");
          removeDataFromLocalStorage("EDIT_VECTOR_CONTROL_RESULT");
          changeMode(PageMode.view);
        })
        .catch((err) => {
          toast.error("Gagal memperbarui data");
          console.error(err);
        });
    } catch (error) {
      toast.error("Terjadi kesalahan saat menyimpan data");
      console.error(error);
    }
  };

  const handleBulkSubmit = async (payload) => {
    try {
      const cleanedPayload = convertEmptyToNull(payload);

      await VectorReportService.bulkCreate(cleanedPayload)
        .then((res) => {
          toast.success("Data berhasil disimpan");
          changeMode(PageMode.view);
        })
        .catch((err) => {
          const errorListHTML = `
                <ul style="margin-left:16px; text-align:left;">
                  ${err?.response?.data?.data
                    .map((err) => `<li>${err}</li>`)
                    .join("")}
                </ul>
              `;

          if (err?.response?.data?.data?.length > 0) {
            toast.error(
              <div>
                <div dangerouslySetInnerHTML={{ __html: errorListHTML }} />
              </div>,
              { autoClose: 8000, position: "top-right" },
            );
          }
        });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchUrbanVillagesByUser();

    if (pageMode === PageMode.edit) {
      loadEditDataFromStorage();
    }
  }, [fetchUrbanVillagesByUser, pageMode, loadEditDataFromStorage]);

  useEffect(() => {
    if (pageMode !== PageMode.add) return;

    setIsDisabledNetReceivedKelambuMassal(false);
    setIsDisabledNetReceivedKelambuMassalFokus(false);

    const uvId = formData.urbanvillage_id;
    if (!uvId) return;

    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, "0");
    const ym = `${year}-${month}`;

    if (showFields["Kelambu Massal Total"]) {
      fetchPrevKelambuMassalDiterimaDesa(ym, "Kelambu Massal Total", uvId);
    }
    if (showFields["Kelambu Massal Fokus"]) {
      fetchPrevKelambuMassalDiterimaDesa(ym, "Kelambu Massal Fokus", uvId);
    }
  }, [
    formData.urbanvillage_id,
    pageMode,
    showFields["Kelambu Massal Total"],
    showFields["Kelambu Massal Fokus"],
  ]);

  useEffect(() => {
    if (pageMode !== PageMode.edit) return;
    if (!editFormData?.vector_controll_type?.includes?.("Kelambu")) return;

    notifyCompareReceivedVsDesk(
      `Edit ${editFormData.vector_controll_type}`,
      editFormData.total_mosquito_nets_received,
      editFormData.total_mosquito_desk,
      "edit",
    );
  }, [
    pageMode,
    editFormData.vector_controll_type,
    editFormData.total_mosquito_nets_received,
    editFormData.total_mosquito_desk,
  ]);

  useEffect(() => {
    if (pageMode !== PageMode.edit) {
      setEditFormValidationError("");
      return;
    }
    if (!editFormData?.vector_controll_type?.includes?.("Kelambu")) {
      setEditFormValidationError("");
      return;
    }
    if (editFormData.is_dusun_level_distribution !== "Ya") {
      setEditFormValidationError("");
      return;
    }

    const totalDistributed = safeToInt(editFormData.total_net_distribution);
    const dusunDistributed = safeToInt(
      editFormData.nets_distributed_sub_village,
    );

    if (totalDistributed > 0 && dusunDistributed > totalDistributed) {
      setEditFormValidationError(
        `Jumlah kelambu terdistribusi (dusun) (${dusunDistributed.toLocaleString()}) tidak boleh melebihi jumlah kelambu terdistribusi (${totalDistributed.toLocaleString()}).`,
      );
    } else {
      setEditFormValidationError("");
    }
  }, [
    pageMode,
    editFormData.vector_controll_type,
    editFormData.is_dusun_level_distribution,
    editFormData.total_net_distribution,
    editFormData.nets_distributed_sub_village,
  ]);

  return (
    <>
      <div className="mt-2">
        <BreadcrumbNav
          items={[
            {
              label: "Laporan Hasil Pengendalian Vektor",
              onclick: () => changeMode(PageMode.view),
            },
            {
              label: pageMode === PageMode.add ? "Tambah Data" : "Edit Data",
            },
          ]}
        />

        <p className="font-weight-bold" style={{ fontSize: 12 }}>
          NOTE: <span className="text-danger">*(WAJIB DIISI)</span>
        </p>
      </div>

      <div className="form-wrapper">
        {pageMode === PageMode.add && (
          <div
            className="vector-village-grid"
            style={{
              marginTop: 20,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
            }}
          >
            <InputSelect
              label={"Pilih desa"}
              name={"urbanvillage_id"}
              placeholder={"Pilih desa"}
              data={urbanvillages.map((item) => ({
                label:
                  item.urbanvillage_id?.split?.("|")?.[1] ||
                  item.urbanvillage_name ||
                  `${item.urbanvillage_id}`,
                value: item.urbanvillage_id,
              }))}
              onChange={async (selected) => {
                const selectedVillage = urbanvillages.find(
                  (village) => village.urbanvillage_id === selected?.value,
                );

                await fetchTotalKelambuDesk(
                  selected?.value,
                  new Date().getFullYear(),
                );

                setFormData((prevData) => ({
                  ...prevData,
                  urbanvillage_id: selected?.value || "",
                  total_population_urbanvillage:
                    selectedVillage?.total_population || 0,
                }));
              }}
              value={
                urbanvillages
                  .map((item) => ({
                    label: item.urbanvillage_name,
                    value: item.urbanvillage_id,
                  }))
                  .find((item) => item.value === formData.urbanvillage_id)
                  ?.label
              }
              isRequired
            />

            <InputField
              label={"Jumlah penduduk desa"}
              name="total_population_urbanvillage"
              type={"number"}
              value={formData.total_population_urbanvillage}
              disabled={true}
              placeholder={
                "Jumlah penduduk desa (Otomatis terisi setelah memilih desa)"
              }
              isRequired
            />
          </div>
        )}

        <div>
          {pageMode === PageMode.edit ? (
            <div className="edit-form-container">
              <div>
                {editFormData.vector_controll_type?.includes("Kelambu") ? (
                  (() => {
                    const isEditDisabled =
                      !editFormData.total_population_target ||
                      parseInt(editFormData.total_population_target) === 0 ||
                      !editFormData.total_mosquito_desk ||
                      parseInt(editFormData.total_mosquito_desk) === 0;

                    return (
                      <div className="kelambu-edit-form">
                        <div className="row">
                          <div className="col-md-6">
                            <InputSelect
                              label="Pilih desa"
                              name="urbanvillage_id"
                              placeholder="Pilih desa"
                              data={urbanvillages.map((item) => ({
                                label:
                                  item.urbanvillage_id?.split?.("|")?.[1] ||
                                  item.urbanvillage_name ||
                                  `${item.urbanvillage_id}`,
                                value: item.urbanvillage_id,
                              }))}
                              onChange={(selected) => {
                                const selectedVillage = urbanvillages.find(
                                  (village) =>
                                    village.urbanvillage_id === selected?.value,
                                );
                                setEditFormData((prev) => ({
                                  ...prev,
                                  urbanvillage_id: selected?.value || "",
                                  total_population_urbanvillage:
                                    selectedVillage?.total_population || "",
                                }));
                              }}
                              value={getOptionByValue(
                                editFormData.urbanvillage_id,
                              )}
                              isRequired
                              isDisabled
                            />
                          </div>

                          <div className="col-md-6">
                            <InputField
                              label="Jumlah penduduk desa"
                              name="total_population_urbanvillage"
                              value={editFormData.total_population_urbanvillage}
                              onChange={handleEditInputChange}
                              type="number"
                              isRequired
                              disabled={true}
                            />
                          </div>
                        </div>

                        <div className="row mt-3">
                          <div className="col-md-6">
                            <InputField
                              label="Jumlah penduduk sasaran"
                              name="total_population_target"
                              value={editFormData.total_population_target}
                              onChange={handleEditInputChange}
                              type="number"
                              disabled
                            />
                          </div>
                          <div className="col-md-6">
                            {editFormData.vector_controll_type ===
                              "Kelambu Massal Total" ||
                            editFormData.vector_controll_type ===
                              "Kelambu Massal Fokus" ? (
                              <InputSelect
                                label="Bulan distribusi kelambu"
                                name="net_distribution_date"
                                placeholder="--PILIH--"
                                data={MonthOptions}
                                onChange={(selected) => {
                                  setEditFormData((prev) => ({
                                    ...prev,
                                    net_distribution_date: `${new Date().getFullYear()}-${
                                      selected?.value
                                    }`,
                                  }));
                                }}
                                value={MonthOptions.find(
                                  (item) =>
                                    item.value ===
                                    editFormData.net_distribution_date?.split(
                                      "-",
                                    )[1],
                                )}
                                disabled
                              />
                            ) : (
                              <InputField
                                label="Tanggal distribusi kelambu"
                                name="net_distribution_date"
                                type="date"
                                value={editFormData.net_distribution_date}
                                onChange={handleEditInputChange}
                                isRequired
                                boldLabel
                              />
                            )}
                          </div>
                        </div>

                        <div className="row mt-3">
                          <div className="col-md-6">
                            <InputField
                              label="Jumlah kelambu hasil desk"
                              name="total_mosquito_desk"
                              value={editFormData.total_mosquito_desk}
                              onChange={handleEditInputChange}
                              type="number"
                              disabled
                            />
                          </div>
                          <div className="col-md-6">
                            <InputField
                              label="Jumlah kelambu terdistribusi"
                              name="total_net_distribution"
                              value={editFormData.total_net_distribution}
                              onChange={handleEditInputChange}
                              type="number"
                              isRequired
                              // disabled={true}
                            />
                          </div>
                        </div>

                        <div className="row mt-3">
                          <div className="col-md-6">
                            <InputField
                              label="Jumlah kelambu yang diterima desa"
                              name="total_mosquito_nets_received"
                              value={editFormData.total_mosquito_nets_received}
                              onChange={handleEditInputChange}
                              type="number"
                              isRequired
                              disabled={isEditDisabled}
                            />
                          </div>
                          <div className="col-md-6">
                            <InputField
                              label="Jumlah kelambu terdistribusi kepada ibu hamil"
                              name="total_nets_distributed_to_pregnant_women"
                              value={
                                editFormData.total_nets_distributed_to_pregnant_women
                              }
                              onChange={handleEditInputChange}
                              type="number"
                              disabled={isEditDisabled}
                            />
                          </div>
                        </div>

                        <div className="row mt-3">
                          <div className="col-md-12">
                            <InputSelect
                              label="Apakah ingin menginformasikan distribusi kelambu sampai tingkat dusun?"
                              name="is_dusun_level_distribution"
                              placeholder="--PILIH--"
                              data={YES_NO_OPTIONS}
                              onChange={(selected) => {
                                setEditFormData((prev) => ({
                                  ...prev,
                                  is_dusun_level_distribution:
                                    selected?.value || "",
                                }));
                              }}
                              value={YES_NO_OPTIONS.find(
                                (item) =>
                                  item.value ===
                                  editFormData.is_dusun_level_distribution,
                              )}
                              isDisabled={isEditDisabled}
                            />
                          </div>
                        </div>

                        {editFormData.is_dusun_level_distribution === "Ya" && (
                          <div className="row mt-3">
                            <div className="col-md-4">
                              <InputField
                                label="Nama dusun"
                                name="sub_village"
                                value={editFormData.sub_village}
                                onChange={handleEditInputChange}
                                isRequired
                              />
                            </div>
                            <div className="col-md-4">
                              <InputField
                                label="Jumlah penduduk dusun"
                                name="sub_village_population"
                                value={editFormData.sub_village_population}
                                onChange={handleEditInputChange}
                                type="number"
                                isRequired
                              />
                            </div>
                            <div className="col-md-4">
                              <InputField
                                label="Jumlah kelambu terdistribusi (dusun)"
                                name="nets_distributed_sub_village"
                                value={
                                  editFormData.nets_distributed_sub_village
                                }
                                onChange={handleEditInputChange}
                                type="number"
                                isRequired
                              />
                            </div>
                          </div>
                        )}

                        {/* Alert validasi real-time */}
                        {editFormValidationError && (
                          <div className="alert alert-danger mt-3" role="alert">
                            {editFormValidationError}
                          </div>
                        )}
                      </div>
                    );
                  })()
                ) : editFormData.vector_controll_type === "IRS" ? (
                  <div className="irs-edit-form">
                    <div className="row">
                      <div className="col-md-6">
                        <InputSelect
                          label="Pilih desa"
                          name="urbanvillage_id"
                          placeholder="Pilih desa"
                          data={urbanvillages.map((item) => ({
                            label:
                              item.urbanvillage_id?.split?.("|")?.[1] ||
                              item.urbanvillage_name ||
                              `${item.urbanvillage_id}`,
                            value: item.urbanvillage_id,
                          }))}
                          onChange={(selected) => {
                            const selectedVillage = urbanvillages.find(
                              (village) =>
                                village.urbanvillage_id === selected?.value,
                            );
                            setEditFormData((prev) => ({
                              ...prev,
                              urbanvillage_id: selected?.value || "",
                              total_population_urbanvillage:
                                selectedVillage?.total_population || "",
                            }));
                          }}
                          value={getOptionByValue(editFormData.urbanvillage_id)}
                          isRequired
                          disabled
                        />
                      </div>

                      <div className="col-md-6">
                        <InputField
                          label="Jumlah penduduk desa"
                          name="total_population_urbanvillage"
                          value={editFormData.total_population_urbanvillage}
                          onChange={handleEditInputChange}
                          type="number"
                          isRequired
                          disabled
                        />
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-6">
                        <InputField
                          label="Tanggal IRS"
                          name="irs_date"
                          type="date"
                          value={editFormData.irs_date}
                          onChange={handleEditInputChange}
                          isRequired
                        />
                      </div>

                      <div className="col-md-6">
                        <InputSelect
                          label="Jenis insektisida"
                          name="insecticide_type"
                          placeholder="--PILIH--"
                          data={INSECTICIDE_TYPE_OPTIONS}
                          onChange={(selected) => {
                            setEditFormData((prev) => ({
                              ...prev,
                              insecticide_type: selected?.value || "",
                            }));
                          }}
                          value={INSECTICIDE_TYPE_OPTIONS.find(
                            (item) =>
                              item.value === editFormData.insecticide_type,
                          )}
                          isRequired
                        />
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-4">
                        <InputField
                          label="Jumlah insektisida yang digunakan"
                          name="total_insecticide"
                          value={editFormData.total_insecticide}
                          onChange={handleEditInputChange}
                          type="number"
                          isRequired
                        />
                      </div>

                      <div className="col-md-4">
                        <InputSelect
                          label="Satuan"
                          name="total_insecticide_unit"
                          placeholder="--PILIH--"
                          data={INSECTICIDE_UNIT_OPTIONS}
                          onChange={(selected) => {
                            setEditFormData((prev) => ({
                              ...prev,
                              total_insecticide_unit: selected?.value || "",
                            }));
                          }}
                          value={INSECTICIDE_UNIT_OPTIONS.find(
                            (item) =>
                              item.value ===
                              editFormData.total_insecticide_unit,
                          )}
                          isRequired
                        />
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-6">
                        <InputField
                          label="Jumlah total sasaran rumah"
                          name="total_targeted_house"
                          value={editFormData.total_targeted_house}
                          onChange={handleEditInputChange}
                          type="number"
                          isRequired
                        />
                      </div>

                      <div className="col-md-6">
                        <InputField
                          label="Jumlah total rumah yang disemprot"
                          name="total_sprayed_house"
                          value={editFormData.total_sprayed_house}
                          onChange={handleEditInputChange}
                          type="number"
                          isRequired
                        />
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-6">
                        <InputSelect
                          label="Apakah ingin menginformasikan intervensi sampai tingkat dusun?"
                          name="is_dusun_level_intervention"
                          placeholder="--PILIH--"
                          data={YES_NO_OPTIONS}
                          onChange={(selected) => {
                            setEditFormData((prev) => ({
                              ...prev,
                              is_dusun_level_intervention:
                                selected?.value || "",
                            }));
                          }}
                          value={YES_NO_OPTIONS.find(
                            (item) =>
                              item.value ===
                              editFormData.is_dusun_level_intervention,
                          )}
                        />
                      </div>
                    </div>

                    {editFormData.is_dusun_level_intervention === "Ya" && (
                      <div className="row mt-3">
                        <div className="col-md-3">
                          <InputField
                            label="Nama dusun"
                            name="sub_village"
                            value={editFormData.sub_village}
                            onChange={handleEditInputChange}
                            isRequired
                          />
                        </div>
                        <div className="col-md-3">
                          <InputField
                            label="Jumlah penduduk dusun"
                            name="sub_village_population"
                            value={editFormData.sub_village_population}
                            onChange={handleEditInputChange}
                            type="number"
                            isRequired
                          />
                        </div>
                        <div className="col-md-3">
                          <InputField
                            label="Jumlah total sasaran rumah (dusun)"
                            name="targeted_houses_sub_village"
                            value={editFormData.targeted_houses_sub_village}
                            onChange={handleEditInputChange}
                            type="number"
                            isRequired
                          />
                        </div>
                        <div className="col-md-3">
                          <InputField
                            label="Jumlah total rumah yang disemprot (dusun)"
                            name="sprayed_houses_sub_village"
                            value={editFormData.sprayed_houses_sub_village}
                            onChange={handleEditInputChange}
                            type="number"
                            isRequired
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="alert alert-warning">
                    Tipe pengendalian vektor tidak dikenali:{" "}
                    {editFormData.vector_controll_type}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <VectorControlTabs
              activeTab={activeTab}
              onTabChange={handleTabChangeWithData}
            >
              {(activeTabData) => (
                <div className="tab-content-form">
                  {pageMode === PageMode.add && !showFields[activeTab] && (
                    <div className="w-full d-flex justify-content-center">
                      <Button
                        onClick={() => handleAddButton(activeTab)}
                        variant={"primary"}
                      >
                        Tambah Data {activeTabData?.fullName}
                      </Button>
                    </div>
                  )}

                  {showFields[activeTab] && (
                    <>
                      {activeTab === "Kelambu Massal Total" && (
                        <KelambuMassalTotalFields
                          formData={formData}
                          handleInputChange={handleInputChange}
                          setFormData={setFormData}
                          memoizedValues={memoizedValues}
                          disabledNetReceived={
                            isDisabledNetReceivedKelambuMassal
                          }
                          inputDisabled={
                            !formData.kelambu_massal_total
                              .total_population_target ||
                            parseInt(
                              formData.kelambu_massal_total
                                .total_population_target,
                            ) === 0 ||
                            !formData.kelambu_massal_total
                              .total_mosquito_desk ||
                            parseInt(
                              formData.kelambu_massal_total.total_mosquito_desk,
                            ) === 0
                          }
                          fetchPrevKelambuMassalDiterimaDesa={
                            fetchPrevKelambuMassalDiterimaDesa
                          }
                          fetchTotalKelambuDesk={fetchTotalKelambuDesk}
                        />
                      )}
                      {activeTab === "Kelambu Massal Fokus" && (
                        <KelambuMassalFokusFields
                          formData={formData}
                          handleInputChange={handleInputChange}
                          setFormData={setFormData}
                          memoizedValues={memoizedValues}
                          disabledNetReceived={
                            isDisabledNetReceivedKelambuMassalFokus
                          }
                          inputDisabled={
                            !formData.kelambu_massal_fokus
                              .total_population_target ||
                            parseInt(
                              formData.kelambu_massal_fokus
                                .total_population_target,
                            ) === 0 ||
                            !formData.kelambu_massal_fokus
                              .total_mosquito_desk ||
                            parseInt(
                              formData.kelambu_massal_fokus.total_mosquito_desk,
                            ) === 0
                          }
                          fetchPrevKelambuMassalDiterimaDesa={
                            fetchPrevKelambuMassalDiterimaDesa
                          }
                          fetchTotalKelambuDesk={fetchTotalKelambuDesk}
                        />
                      )}
                      {activeTab === "Kelambu PE 1-2-5" && (
                        <KelambuPE125Fields
                          formData={formData}
                          handleInputChange={handleInputChange}
                          setFormData={setFormData}
                          memoizedValues={memoizedValues}
                          inputDisabled={
                            !formData.kelambu_pe_125.total_population_target ||
                            parseInt(
                              formData.kelambu_pe_125.total_population_target,
                            ) === 0 ||
                            !formData.kelambu_pe_125.total_mosquito_desk ||
                            parseInt(
                              formData.kelambu_pe_125.total_mosquito_desk,
                            ) === 0
                          }
                        />
                      )}
                      {activeTab === "Kelambu KLB/Bencana" && (
                        <KelambuKLBBencanaFields
                          formData={formData}
                          handleInputChange={handleInputChange}
                          setFormData={setFormData}
                          memoizedValues={memoizedValues}
                          inputDisabled={
                            !formData.kelambu_klb_bencana
                              .total_population_target ||
                            parseInt(
                              formData.kelambu_klb_bencana
                                .total_population_target,
                            ) === 0 ||
                            !formData.kelambu_klb_bencana.total_mosquito_desk ||
                            parseInt(
                              formData.kelambu_klb_bencana.total_mosquito_desk,
                            ) === 0
                          }
                        />
                      )}
                      {activeTab === "IRS" && (
                        <IRSFields
                          formData={formData}
                          handleInputChange={handleInputChange}
                          setFormData={setFormData}
                          memoizedValues={memoizedValues}
                        />
                      )}

                      {pageMode === PageMode.add && (
                        <div className="d-flex gap-2 mt-3">
                          <Button
                            variant="danger"
                            onClick={() => {
                              setShowFields((prev) => ({
                                ...prev,
                                [activeTab]: false,
                              }));

                              setFormData((prev) => {
                                const getFieldGroupKey = (tabKey) => {
                                  switch (tabKey) {
                                    case "Kelambu Massal Total":
                                      return "kelambu_massal_total";
                                    case "Kelambu Massal Fokus":
                                      return "kelambu_massal_fokus";
                                    case "Kelambu PE 1-2-5":
                                      return "kelambu_pe_125";
                                    case "Kelambu KLB/Bencana":
                                      return "kelambu_klb_bencana";
                                    case "IRS":
                                      return "irs";
                                    default:
                                      return null;
                                  }
                                };
                                const fieldGroupKey =
                                  getFieldGroupKey(activeTab);
                                if (!fieldGroupKey) return prev;

                                const defaultStates = {
                                  kelambu_massal_total: {
                                    net_distribution_date: "",
                                    total_mosquito_nets_received: "",
                                    total_net_distribution: "",
                                    total_mosquito_desk: "",
                                    total_population_target: "",
                                    total_nets_distributed_to_pregnant_women:
                                      "",
                                    is_dusun_level_distribution: "",
                                    total_sub_villages: "",
                                    sub_villages_data: [],
                                  },
                                  kelambu_massal_fokus: {
                                    net_distribution_date: "",
                                    total_mosquito_nets_received: "",
                                    total_net_distribution: "",
                                    total_mosquito_desk: "",
                                    total_population_target: "",
                                    total_nets_distributed_to_pregnant_women:
                                      "",
                                    is_dusun_level_distribution: "",
                                    total_sub_villages: "",
                                    sub_villages_data: [],
                                  },
                                  kelambu_pe_125: {
                                    net_distribution_date: "",
                                    total_mosquito_nets_received: "",
                                    total_net_distribution: "",
                                    total_mosquito_desk: "",
                                    total_population_target: "",
                                    total_nets_distributed_to_pregnant_women:
                                      "",
                                    is_dusun_level_distribution: "",
                                    total_sub_villages: "",
                                    sub_villages_data: [],
                                  },
                                  kelambu_klb_bencana: {
                                    net_distribution_date: "",
                                    total_mosquito_nets_received: "",
                                    total_net_distribution: "",
                                    total_mosquito_desk: "",
                                    total_population_target: "",
                                    total_nets_distributed_to_pregnant_women:
                                      "",
                                    is_dusun_level_distribution: "",
                                    total_sub_villages: "",
                                    sub_villages_data: [],
                                  },
                                  irs: {
                                    irs_date: "",
                                    insecticide_type: "",
                                    total_insecticide: "",
                                    total_insecticide_unit: "",
                                    total_targeted_house: "",
                                    total_sprayed_house: "",
                                    is_dusun_level_intervention: "",
                                    total_sub_villages: "",
                                    sub_villages_data: [],
                                  },
                                };
                                return {
                                  ...prev,
                                  [fieldGroupKey]: defaultStates[fieldGroupKey],
                                };
                              });
                            }}
                          >
                            Batalkan {activeTabData?.fullName}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </VectorControlTabs>
          )}
        </div>

        {Object.values(showFields).some((field) => field === true) && (
          <div style={{ marginTop: 30 }}>
            <div
              style={{
                padding: 20,
                backgroundColor: "#e9f7ef",
                borderRadius: 5,
              }}
            >
              <strong>Data Pengendalian Vektor yang Ditambahkan:</strong>
              <ul className="mb-0 mt-2">
                {Object.keys(showFields)
                  .filter((key) => showFields[key])
                  .map((tabKey) => (
                    <li key={tabKey}>
                      {tabKey === "Kelambu Massal Total"
                        ? "Kelambu Massal"
                        : tabKey}
                    </li>
                  ))}
              </ul>
              <small className="text-muted">
                Total:{" "}
                {
                  Object.values(showFields).filter((field) => field === true)
                    .length
                }{" "}
                jenis pengendalian vektor
              </small>
            </div>
          </div>
        )}

        <div
          className="vector-control-actions"
          style={{
            marginTop: 30,
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant={"secondary"}
            onClick={() => changeMode(PageMode.view)}
          >
            Batalkan
          </Button>
          <Button
            variant={"primary"}
            onClick={() => {
              if (pageMode === PageMode.edit) {
                handleEditSubmit();
                return;
              }

              const getFieldGroupKey = (tabKey) => {
                switch (tabKey) {
                  case "Kelambu Massal Total":
                    return "kelambu_massal_total";
                  case "Kelambu Massal Fokus":
                    return "kelambu_massal_fokus";
                  case "Kelambu PE 1-2-5":
                    return "kelambu_pe_125";
                  case "Kelambu KLB/Bencana":
                    return "kelambu_klb_bencana";
                  case "IRS":
                    return "irs";
                  default:
                    return null;
                }
              };

              let vectorControlDataArray;
              let addedVectorControlTypes;

              addedVectorControlTypes = Object.keys(showFields).filter(
                (tabKey) => showFields[tabKey] === true,
              );

              vectorControlDataArray = addedVectorControlTypes.map((tabKey) => {
                const fieldGroupKey = getFieldGroupKey(tabKey);
                const rawData = fieldGroupKey ? formData[fieldGroupKey] : {};

                const formattedData = { ...rawData };

                const numericFields = [
                  "total_mosquito_nets_received",
                  "total_net_distribution",
                  "total_mosquito_desk",
                  "total_population_target",
                  "total_nets_distributed_to_pregnant_women",
                  "total_insecticide",
                  "total_targeted_house",
                  "total_sprayed_house",
                  "total_sub_villages",
                ];

                numericFields.forEach((field) => {
                  if (formattedData[field]) {
                    formattedData[field] =
                      parseInt(formattedData[field]) || formattedData[field];
                  }
                });

                const cleanedData = {};

                if (tabKey === "IRS") {
                  const irsFields = [
                    "irs_date",
                    "insecticide_type",
                    "total_insecticide",
                    "total_insecticide_unit",
                    "total_targeted_house",
                    "total_sprayed_house",
                    "is_dusun_level_intervention",
                    "total_sub_villages",
                    "sub_villages_data",
                  ];
                  irsFields.forEach((field) => {
                    if (formattedData[field] !== undefined) {
                      cleanedData[field] = formattedData[field];
                    }
                  });
                } else {
                  const kelambuFields = [
                    "net_distribution_month",
                    "net_distribution_date",
                    "total_mosquito_nets_received",
                    "total_net_distribution",
                    "total_mosquito_desk",
                    "total_population_target",
                    "total_nets_distributed_to_pregnant_women",
                    "is_dusun_level_distribution",
                    "total_sub_villages",
                    "sub_villages_data",
                  ];
                  kelambuFields.forEach((field) => {
                    if (formattedData[field] !== undefined) {
                      cleanedData[field] = formattedData[field];
                    }
                  });
                }

                if (
                  !cleanedData.sub_villages_data ||
                  !Array.isArray(cleanedData.sub_villages_data)
                ) {
                  cleanedData.sub_villages_data = [];
                }

                if (
                  cleanedData.total_sub_villages &&
                  cleanedData.sub_villages_data.length === 0
                ) {
                  const subVillageCount =
                    parseInt(cleanedData.total_sub_villages) || 1;
                  for (let i = 0; i < subVillageCount; i++) {
                    if (tabKey === "IRS") {
                      cleanedData.sub_villages_data.push({
                        village_name: `Dusun ${String.fromCharCode(65 + i)}`,
                        population: "0",
                        targeted_houses:
                          cleanedData.total_targeted_house || "0",
                        sprayed_houses: cleanedData.total_sprayed_house || "0",
                      });
                    } else {
                      cleanedData.sub_villages_data.push({
                        village_name: `Dusun ${String.fromCharCode(65 + i)}`,
                        population: "0",
                        nets_distributed:
                          cleanedData.total_net_distribution || "0",
                      });
                    }
                  }
                }

                return {
                  vector_control_type: tabKey,
                  data: cleanedData,
                };
              });

              const validationErrors = [];

              if (!formData.urbanvillage_id) {
                validationErrors.push("Kelurahan/Desa wajib dipilih");
              }
              if (!formData.total_population_urbanvillage) {
                validationErrors.push(
                  "Total populasi kelurahan/desa wajib diisi",
                );
              }

              addedVectorControlTypes.forEach((tabKey) => {
                const fieldGroupKey = getFieldGroupKey(tabKey);
                const data = fieldGroupKey ? formData[fieldGroupKey] : {};
                let tabErrors = [];

                switch (tabKey) {
                  case "Kelambu Massal Total":
                    tabErrors = validateKelambuMassalTotal(data);
                    break;
                  case "Kelambu Massal Fokus":
                    tabErrors = validateKelambuMassalFokus(data);
                    break;
                  case "Kelambu PE 1-2-5":
                    tabErrors = validateKelambuPE125(data);
                    break;
                  case "Kelambu KLB/Bencana":
                    tabErrors = validateKelambuKLBBencana(data);
                    break;
                  case "IRS":
                    tabErrors = validateIRS(data);
                    break;
                }

                if (tabErrors.length > 0) {
                  validationErrors.push(`${tabKey}: ${tabErrors.join(", ")}`);
                }
              });

              const errorListHTML = `
                <ul style="margin-left:16px; text-align:left;">
                  ${validationErrors.map((err) => `<li>${err}</li>`).join("")}
                </ul>
              `;

              if (validationErrors.length > 0) {
                toast.error(
                  <div>
                    <strong>Lengkapi data berikut:</strong>
                    <div dangerouslySetInnerHTML={{ __html: errorListHTML }} />
                  </div>,
                  { autoClose: 8000, position: "top-right" },
                );

                return;
              }

              const extractLocationData = (urbanvillageId) => {
                if (!urbanvillageId) return {};

                const selectedVillage = urbanvillages.find(
                  (village) => village.urbanvillage_id === urbanvillageId,
                );

                return {
                  subdistrict_id: selectedVillage?.subdistrict_id || "",
                  district_id: selectedVillage?.district_id || "",
                  province_id: selectedVillage?.province_id || "",
                };
              };

              const locationData = extractLocationData(
                formData.urbanvillage_id,
              );

              const submitData = {
                urbanvillage_id: formData.urbanvillage_id,
                total_population_urbanvillage:
                  parseInt(formData.total_population_urbanvillage) || 0,
                subdistrict_id: locationData.subdistrict_id,
                district_id: locationData.district_id,
                province_id: locationData.province_id,

                replace_existing: true,

                vector_control_entries: vectorControlDataArray,

                vector_control_types_list: addedVectorControlTypes,
              };

              handleBulkSubmit(submitData);
            }}
            isDisabled={
              pageMode === PageMode.edit
                ? !editFormData.id || !!editFormValidationError
                : !Object.values(showFields).some((field) => field === true)
            }
          >
            {pageMode === PageMode.add ? "Simpan Data" : "Update Data"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default FormComponent;
